import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface AppSettings {
  pontos_progressao: number;
  pontos_progressao_pre2025: number;
  pontos_progressao_2_niveis: number;
  pontos_progressao_2023: number;
  nome_camara: string;
  ciclo_siadap: string;
  ciclo_ano_inicio: string;
  ano_progressao: string;
  email_notificacoes: string;
  pontos_excelente: number;
  pontos_muito_bom: number;
  pontos_bom: number;
  pontos_regular: number;
  pontos_inadequado: number;
  pontos_excelente_2023: number;
  pontos_muito_bom_2023: number;
  pontos_bom_2023: number;
  pontos_regular_2023: number;
  pontos_inadequado_2023: number;
  pontos_excelente_pre2023: number;
  pontos_relevante_pre2023: number;
  pontos_adequado_pre2023: number;
  pontos_inadequado_pre2023: number;
}

interface AppSettingsContextType {
  settings: AppSettings;
  loading: boolean;
  refetch: () => Promise<void>;
}

export const DEFAULT_SETTINGS: AppSettings = {
  pontos_progressao: 8,
  pontos_progressao_pre2025: 10,
  pontos_progressao_2_niveis: 20,
  pontos_progressao_2023: 8,
  nome_camara: 'Câmara Municipal',
  ciclo_siadap: '2023–2024',
  ciclo_ano_inicio: '2023',
  ano_progressao: '2025',
  email_notificacoes: '',
  pontos_excelente: 3,
  pontos_muito_bom: 2,
  pontos_bom: 1.5,
  pontos_regular: 1,
  pontos_inadequado: 0,
  pontos_excelente_2023: 6,
  pontos_muito_bom_2023: 4,
  pontos_bom_2023: 3,
  pontos_regular_2023: 2,
  pontos_inadequado_2023: 0,
  pontos_excelente_pre2023: 6,
  pontos_relevante_pre2023: 4,
  pontos_adequado_pre2023: 2,
  pontos_inadequado_pre2023: -2,
};

function settingsFromMap(map: Record<string, string | number | boolean>): AppSettings {
  return {
    pontos_progressao: Number(map['pontos_progressao'] ?? 8),
    pontos_progressao_pre2025: Number(map['pontos_progressao_pre2025'] ?? 10),
    pontos_progressao_2_niveis: Number(map['pontos_progressao_2_niveis'] ?? 20),
    pontos_progressao_2023: Number(map['pontos_progressao_2023'] ?? 8),
    nome_camara: map['nome_camara'] ?? 'Câmara Municipal',
    ciclo_siadap: String(map['ciclo_siadap'] ?? '2023–2024'),
    ciclo_ano_inicio: String(map['ciclo_ano_inicio'] ?? '2023'),
    ano_progressao: map['ano_progressao'] ?? '2025',
    email_notificacoes: map['email_notificacoes'] ?? '',
    pontos_excelente: Number(map['pontos_excelente'] ?? 3),
    pontos_muito_bom: Number(map['pontos_muito_bom'] ?? 2),
    pontos_bom: Number(map['pontos_bom'] ?? 1.5),
    pontos_regular: Number(map['pontos_regular'] ?? 1),
    pontos_inadequado: Number(map['pontos_inadequado'] ?? 0),
    pontos_excelente_2023: Number(map['pontos_excelente_2023'] ?? 6),
    pontos_muito_bom_2023: Number(map['pontos_muito_bom_2023'] ?? 4),
    pontos_bom_2023: Number(map['pontos_bom_2023'] ?? 3),
    pontos_regular_2023: Number(map['pontos_regular_2023'] ?? 2),
    pontos_inadequado_2023: Number(map['pontos_inadequado_2023'] ?? 0),
    pontos_excelente_pre2023: Number(map['pontos_excelente_pre2023'] ?? 6),
    pontos_relevante_pre2023: Number(map['pontos_relevante_pre2023'] ?? 4),
    pontos_adequado_pre2023: Number(map['pontos_adequado_pre2023'] ?? 2),
    pontos_inadequado_pre2023: Number(map['pontos_inadequado_pre2023'] ?? -2),
  };
}

const AppSettingsContext = createContext<AppSettingsContextType>({
  settings: DEFAULT_SETTINGS,
  loading: false,
  refetch: async () => {},
});

export function AppSettingsProvider({ children }: { children: React.ReactNode }) {
  const { user, profile, profileLoading } = useAuth();
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);

  const fetchSettings = useCallback(async (municipioId: string) => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('key, value')
        .eq('municipio_id', municipioId);

      if (data && data.length > 0) {
        const map: Record<string, string> = {};
        data.forEach((row: { key: string; value: string }) => { map[row.key] = row.value; });
        setSettings(settingsFromMap(map));
      }
    } catch {
      // usar defaults em caso de erro
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setSettings(DEFAULT_SETTINGS);
      return;
    }
    // Aguardar perfil mas não bloquear indefinidamente
    if (profileLoading) return;

    const municipioId = profile?.municipio_id ?? '00000000-0000-0000-0000-000000000001';
    fetchSettings(municipioId);
  }, [user, profile, profileLoading, fetchSettings]);

  const refetch = useCallback(async () => {
    const municipioId = profile?.municipio_id ?? '00000000-0000-0000-0000-000000000001';
    await fetchSettings(municipioId);
  }, [profile, fetchSettings]);

  return (
    <AppSettingsContext.Provider value={{ settings, loading, refetch }}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettings() {
  return useContext(AppSettingsContext);
}
